import React, { useState, useEffect } from "react";
import config from "../../config";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";

const ExpenseModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        date: "",
        expenseType: "",
        expenseAccount: "",
        expenseAmount: "",
        expenseCategory: "",
        invoiceId: "",
        notes: "",
    });
    const bid = localStorage.getItem("branch_id");
    const [staffData, setStaffData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [amountPaid, setAmountPaid] = useState("");
    const [completedDate, setCompletedDate] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [errors, setErrors] = useState({ completedDate: "", dueDate: "" });
    const [errorMessage, setErrorMessage] = useState("");

    // Handle date input change with validation
    const handleDateChange = (e, field) => {
        const value = e.target.value;
        setErrors((prev) => ({ ...prev, [field]: "" }));

        if (field === "completedDate") setCompletedDate(value);
        if (field === "dueDate") setDueDate(value);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const handleAmountPaidChange = (e) => {
        const value = e.target.value;
        setAmountPaid(value);
        
        // Validate amount paid against expense amount
        if (formData.expenseAmount && parseFloat(value) > parseFloat(formData.expenseAmount)) {
            setErrorMessage("Amount paid cannot exceed expense amount!");
        } else {
            setErrorMessage("");
        }
    };

    const handleSubmit = async () => {
        // Validate dates
        if (!formData.date) {
            toast.error("Please select a date");
            return;
        }

        // Validate amount paid
        if (amountPaid && parseFloat(amountPaid) > parseFloat(formData.expenseAmount)) {
            toast.error("Amount paid cannot exceed expense amount");
            return;
        }

        const payload = {
            date: formData.date,
            category: formData.category,
            expense_type: formData.expenseType,
            expense_account: formData.expenseAccount,
            expense_amount: parseFloat(formData.expenseAmount) || 0,
            expense_category: [formData.expenseCategory || null],
            invoice_id: formData.invoiceId || "",
            comment: formData.notes,
            amount_paid: amountPaid,
            completed_date: completedDate,
            due_date: dueDate || "",
        };

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${config.apiUrl}/api/swalook/expense_management/?branch_name=${bid}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Token ${token}`,
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (response.ok) {
                toast.success("Expense added successfully");
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                const errorData = await response.json();
                toast.error(`Error: ${errorData.message}`);
            }
        } catch (error) {
            toast.error("An error occurred. Please try again.");
        }
    };

    useEffect(() => {
        fetchStaffData();
        fetchExpenseCategory();
    }, []);

    const fetchStaffData = async () => {
        const token = localStorage.getItem("token");
        try {
            const staffResponse = await fetch(
                `${config.apiUrl}/api/swalook/staff/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!staffResponse.ok) {
                throw new Error("Network response was not ok");
            }

            const staffData = await staffResponse.json();
            const staffArray = Array.isArray(staffData.table_data)
                ? staffData.table_data
                : [];

            const formattedOptions = staffArray.map((staff) => ({
                label: `${staff.staff_data.staff_name} (${staff.staff_data.staff_role})`,
                value: staff.staff_data.staff_name,
            }));

            setStaffData(formattedOptions);
        } catch (error) {
            console.error("Error fetching staff data:", error);
            setStaffData([]);
        }
    };

    const fetchExpenseCategory = async () => {
        try {
            const branchName = localStorage.getItem("branch_name");
            const token = localStorage.getItem("token");

            if (!branchName || !token) {
                throw new Error("Branch name or token is missing.");
            }

            const response = await fetch(
                `${config.apiUrl}/api/swalook/expense_category/?branch_name=${branchName}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Token ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch data: ${response.statusText}`);
            }

            const data = await response.json();
            const transformedData = data.data.map((item) => ({
                id: item.id,
                categories: JSON.parse(
                    item.vendor_expense_type.replace(/'/g, '"')
                ),
            }));

            setCategoryData(transformedData);
        } catch (err) {
            console.error("Error fetching expense category data:", err);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem("token");

        axios
            .get(
                `${config.apiUrl}/api/swalook/product_category/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            )
            .then((response) => {
                if (response.data.status) {
                    setCategories(response.data.data);
                } else {
                    setPopupMessage("Failed to fetch categories.");
                    setShowPopup(true);
                }
            })
            .catch((err) => {
                console.error("Error fetching categories:", err);
                setPopupMessage("Failed to fetch categories.");
                setShowPopup(true);
            });
    }, [bid]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Toaster />
            <div className="bg-white rounded-2xl w-full max-w-5xl max-h-screen m-4 overflow-y-auto p-4">
                <div className="sticky top-0 bg-white p-6 border-b flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">
                        New Expense
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-red-500 hover:text-red-700 text-3xl font-bold"
                    >
                        &times;
                    </button>
                </div>

                {/* Expense Details Section */}
                <div className="p-6">
                    <h3 className="text-lg font-semibold mb-4">
                        Expense Details:
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                        <select
                            name="expenseType"
                            value={formData.expenseType}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            <option value="">Expense Type*</option>
                            <option value="Invoice">Purchase Invoice</option>
                            <option value="Miscellaneous Expenses">
                                Miscellaneous Expenses
                            </option>
                            <option value="Staff Salary">Staff Salary</option>
                        </select>
                        <select
                            name="expenseAccount"
                            value={formData.expenseAccount}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Expense Account*</option>
                            <option value="Company Expense">
                                Company Expense
                            </option>
                            {staffData.map((staff, index) => (
                                <option key={index} value={staff.value}>
                                    {staff.label}
                                </option>
                            ))}
                        </select>
                        <input
                            type="number"
                            name="expenseAmount"
                            placeholder="Expense Amount*"
                            value={formData.expenseAmount}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                        <select
                            name="expenseCategory"
                            value={formData.expenseCategory}
                            onChange={handleChange}
                            disabled={formData.expenseType === "Invoice"}
                            className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            <option value="">Expense Category*</option>
                            {categoryData.flatMap((category) =>
                                category.categories.map((type, index) => (
                                    <option
                                        key={`${category.id}-${index}`}
                                        value={category.id}
                                    >
                                        {type}
                                    </option>
                                ))
                            )}
                        </select>
                        <input
                            type="text"
                            name="invoiceId"
                            value={formData.invoiceId}
                            onChange={handleChange}
                            placeholder="Invoice ID (if any)"
                            className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Payment Details Section */}
                    <h3 className="text-lg font-semibold mb-4">
                        Payment Details:
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div>
                            <span>Amount Paid</span>
                            <input
                                type="number"
                                placeholder="Enter Amount Paid"
                                value={amountPaid}
                                onChange={handleAmountPaidChange}
                                className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {errorMessage && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errorMessage}
                                </p>
                            )}
                        </div>
                        <div>
                            <span>Completed Date</span>
                            <input
                                type="date"
                                value={completedDate}
                                onChange={(e) =>
                                    handleDateChange(e, "completedDate")
                                }
                                className={`w-full p-3 border rounded-full focus:outline-none focus:ring-2 ${
                                    errors.completedDate
                                        ? "border-red-500"
                                        : "border-gray-300 focus:ring-blue-500"
                                }`}
                            />
                            {errors.completedDate && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.completedDate}
                                </p>
                            )}
                        </div>
                        <div>
                            <span>Due Date </span>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => handleDateChange(e, "dueDate")}
                                className={`w-full p-3 border rounded-full focus:outline-none focus:ring-2 ${
                                    errors.dueDate
                                        ? "border-red-500"
                                        : "border-gray-300 focus:ring-blue-500"
                                }`}
                            />
                            {errors.dueDate && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.dueDate}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Notes Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-2">Notes:</h3>
                        <textarea
                            placeholder="Additional Notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                        ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center">
                        <button
                            onClick={handleSubmit}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-full transition duration-300"
                        >
                            Create Expense
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpenseModal;